import { ChevronsUpDown } from "lucide-react";
import React, { forwardRef } from "react";
import { Link } from "react-router-dom";

import { useGetAuthUser } from "@/api/get-user";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { cn } from "@/lib/utils";

import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";

export const UserProfileButton = forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement>
>((props, ref) => {
  const { data: user, isPending, isSuccess, isError } = useGetAuthUser();
  const { organisation } = useOrganisation();

  if (isPending) {
    return (
      <div
        ref={ref}
        className="-mx-2 flex flex-1 items-center gap-2 rounded-md p-2"
        {...props}
      >
        <div>
          <Skeleton className="size-10 h-10 w-10 rounded-full bg-border" />
        </div>
        <div className="ml-2 w-full space-y-1">
          <Skeleton className="h-2 w-2/3 bg-border" />
          <Skeleton className="h-2 w-3/5 bg-border" />
        </div>
      </div>
    );
  }

  if (isError || !isSuccess) {
    return (
      <Link to="/login">
        <Button variant="outline">Login</Button>
      </Link>
    );
  }

  return (
    <div
      ref={ref}
      className={cn(
        "-mx-2 flex flex-1 items-center gap-2 rounded-md border border-transparent p-2 transition-all hover:cursor-pointer hover:border-border hover:bg-background hover:shadow-sm",
        "data-[state=open]:border-border data-[state=open]:bg-background data-[state=open]:shadow-sm",
      )}
      {...props}
    >
      <div className="relative">
        <Avatar>
          <AvatarImage src={user.avatarUrl ?? undefined} />
          <AvatarFallback className="bg-border text-muted">
            {user.given_name?.[0]}
            {user.family_name?.[0]}
          </AvatarFallback>
        </Avatar>
        {organisation.logoURL && (
          <Avatar className="absolute bottom-0 right-0 size-4 border border-white">
            <AvatarImage src={organisation.logoURL} />
          </Avatar>
        )}
      </div>
      <p className="flex min-w-0 flex-1 flex-col">
        <span
          className="block truncate text-left text-sm font-semibold text-secondary"
          data-sentry-mask
        >
          {user.given_name} {user.family_name}
        </span>
        <span className="block truncate text-left text-xs font-normal text-muted">
          {organisation?.name}
        </span>
      </p>
      <ChevronsUpDown className="size-4 text-muted-foreground" />
    </div>
  );
});

UserProfileButton.displayName = "UserProfileButton";
